import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapPin, RefreshCw, TrendingUp, Lightbulb, Trophy, Beaker } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCampusAuth } from "@/contexts/CampusAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [7, 30, 90];

function pct(rate) {
  return `${(rate * 100).toFixed(1)} %`;
}

/**
 * Admin-Dashboard: Conversions pro Bundesland für die Ads-LP `/wer-darf-was`.
 * Datenquelle: GET /api/admin/leadmagnet/bundesland-stats?days=N
 */
export default function CampusBundeslandStats() {
  const { user } = useCampusAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (d = days) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/leadmagnet/bundesland-stats`, {
        params: { days: d },
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Konnte Statistik nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); }, [days]);

  const maxLeads = useMemo(() => {
    if (!data?.by_bundesland) return 1;
    return Math.max(1, ...data.by_bundesland.map((b) => b.leads));
  }, [data]);

  if (!user?.is_admin) {
    return (
      <div data-testid="bl-stats-locked" className="rounded-3xl border border-cream-300 bg-white p-8 text-sand-700">
        Diese Seite ist nur für Admins sichtbar.
      </div>
    );
  }

  const top3 = data?.top_3 ?? [];
  const noData = data && data.total_leads === 0;
  const activeBls = data?.by_bundesland?.filter((b) => b.leads > 0) ?? [];

  return (
    <div className="space-y-8" data-testid="bl-stats-page">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="h-5 w-5 text-terra" strokeWidth={1.8} />
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay-dark">
              Admin · Google-Ads Performance
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-sand-800 leading-tight">
            Conversions pro Bundesland
          </h1>
          <p className="mt-2 text-sand-600 text-[14.5px] max-w-2xl">
            Performance der Ads-Landingpage{" "}
            <code className="bg-cream-100 px-1.5 py-0.5 rounded text-[12.5px] text-brand-darkred">/wer-darf-was</code>{" "}
            für den Atlas-Lead-Magnet. Wer-darf-was-Leads, die später Helden-User wurden, zählen als Conversion.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full bg-cream-100 p-1" data-testid="bl-stats-period">
            {PERIODS.map((d) => (
              <button key={d} type="button" onClick={() => setDays(d)}
                data-testid={`bl-stats-period-${d}`}
                className={`rounded-full h-9 px-4 text-[12.5px] font-semibold transition-colors ${days === d ? "bg-sand-800 text-cream-50" : "text-sand-600 hover:text-sand-800"}`}>
                {d}&nbsp;Tage
              </button>
            ))}
          </div>
          <Button onClick={() => load(days)} disabled={loading} variant="outline"
            data-testid="bl-stats-reload"
            className="rounded-full h-11 px-5 border-clay text-sand-800 hover:bg-cream-100">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Neu laden
          </Button>
        </div>
      </div>

      {error && (
        <div data-testid="bl-stats-error" className="rounded-2xl border border-terra/40 bg-terra/10 text-terra-dark p-4">
          {error}
        </div>
      )}

      {/* TOTALS */}
      {data && (
        <div className="grid sm:grid-cols-3 gap-4" data-testid="bl-stats-totals">
          <KpiCard label="Leads gesamt" value={data.total_leads} accent="text-sand-800" />
          <KpiCard label="Wurden Helden-User" value={data.total_converted} accent="text-terra" />
          <KpiCard label="Conversion-Rate" value={pct(data.conversion_rate)} accent="text-brand-darkred" />
        </div>
      )}

      {/* A/B-TEST BREAKDOWN */}
      {data?.by_variant && (
        <div data-testid="bl-stats-ab" className="rounded-3xl border border-cream-300 bg-white p-7">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-darkred/12 text-brand-darkred">
                <Beaker className="h-5 w-5" strokeWidth={1.8} />
              </span>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">A/B-Test · Hero-Tonalität</div>
                <h2 className="font-display text-xl text-sand-800 leading-tight mt-1">Editorial vs. Direct-Response</h2>
              </div>
            </div>
            {data.ab_winner && (
              <span data-testid="bl-stats-ab-winner"
                className="inline-flex items-center gap-1.5 rounded-full bg-sage-dark text-cream-50 px-4 h-9 text-[12px] uppercase tracking-[0.18em] font-bold">
                <Trophy className="h-3.5 w-3.5" strokeWidth={2.2} /> Gewinner: {data.ab_winner === "A" ? "Editorial" : "Direct-Response"}
              </span>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            {data.by_variant.map((v) => {
              const isWinner = data.ab_winner === v.label;
              const noData = v.leads === 0;
              return (
                <div key={v.label}
                  data-testid={`bl-stats-ab-${v.label}`}
                  className={`rounded-2xl border p-5 ${isWinner ? "border-sage-dark/40 bg-sage-light/30" : "border-cream-300 bg-cream-50"}`}>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500">{v.name}</div>
                  <div className="mt-2 font-display text-2xl text-sand-800">
                    {noData ? "—" : pct(v.conversion_rate)}
                  </div>
                  <div className="mt-1 text-[12.5px] text-sand-600 tabular-nums">
                    {v.converted} von {v.leads} Leads
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-5 text-[12.5px] text-sand-500 leading-relaxed max-w-2xl">
            Signifikanz wird ab je <b>30 Leads pro Variante</b> + mind. <b>2&nbsp;%-Punkte</b> Rate-Differenz angezeigt.
            Solange läuft das Experiment.
            {data.ab_winner && (
              <>
                {" "}Empfehlung: Den Hero-Hook der Variante <b>{data.ab_winner}</b> als
                Default fixieren — A/B-Toggle entfernen, Budget skalieren.
              </>
            )}
          </p>
        </div>
      )}

      {/* INSIGHT BOX */}
      {data && (
        <div data-testid="bl-stats-insight" className="rounded-3xl border border-cream-300 bg-white p-7">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-terra/15 text-terra shrink-0">
              <Lightbulb className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl text-sand-800 leading-tight">Budget-Empfehlung</h2>
              {noData ? (
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  Noch keine Leads von der Ads-LP <code className="bg-cream-100 px-1.5 py-0.5 rounded">/wer-darf-was</code> in den letzten {days} Tagen.
                  Sobald die Google-Ads-Kampagne läuft und das BL-Tracking aktiv ist, erscheinen hier die Top-Bundesländer für Budget-Umschichtung.
                </p>
              ) : top3.length === 0 ? (
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  Es gibt Leads, aber keine mit Bundesland-Tracking. Prüfe, ob die Ads-LP <code>/wer-darf-was</code> den{" "}
                  <code>source</code>-Parameter korrekt setzt (`ads-lp-wer-darf-was:BL`).
                </p>
              ) : (
                <p className="mt-2 text-sand-600 text-[14.5px] leading-relaxed">
                  <b>Top-3-Bundesländer ({days} Tage):</b>{" "}
                  {top3.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 mr-2 rounded-full bg-terra/15 text-terra px-2.5 h-6 text-[11px] uppercase tracking-[0.18em] font-bold">
                      <Trophy className="h-3 w-3" strokeWidth={2} /> {c}
                    </span>
                  ))}
                  <br className="hidden sm:block" />
                  Empfehlung: In Google Ads <b>Geo-Targeting</b> auf diese Regionen verstärken (z. B. +30 % Bid-Adjustment). Schwache BLs ggf. pausieren.
                </p>
              )}
              {data.untracked_leads > 0 && (
                <p className="mt-3 text-[12.5px] text-sand-500">
                  {data.untracked_leads} Leads ohne BL-Code (z. B. Bundle-Funnel oder ältere Eintragungen) – im Insight nicht berücksichtigt.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PRO-BL-TABELLE / BAR-CHART */}
      {data && activeBls.length > 0 && (
        <div data-testid="bl-stats-table" className="rounded-3xl border border-cream-300 bg-cream-50 p-7">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-5">
            Aktive Bundesländer · letzte {days} Tage
          </div>
          <ul className="space-y-3">
            {activeBls.map((b, i) => {
              const widthPct = (b.leads / maxLeads) * 100;
              const isTop = top3.includes(b.code);
              return (
                <li key={b.code} data-testid={`bl-stats-row-${b.code}`}
                  className={`rounded-2xl border ${isTop ? "border-terra/40 bg-white" : "border-cream-300 bg-white"} p-4`}>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-[11px] font-semibold text-sand-500 w-6 shrink-0">#{i + 1}</span>
                      <span className={`inline-flex items-center justify-center rounded-md w-10 h-7 text-[11px] font-bold ${isTop ? "bg-terra text-white" : "bg-sand-800 text-cream-50"}`}>
                        {b.code}
                      </span>
                      <span className="text-[14px] text-sand-800 font-medium truncate">{b.name}</span>
                      {isTop && (
                        <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-terra/15 text-terra px-2 h-5 text-[10px] uppercase tracking-[0.16em] font-bold">
                          <Trophy className="h-3 w-3" strokeWidth={2.2} /> Top {top3.indexOf(b.code) + 1}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 shrink-0 tabular-nums">
                      <span className="text-[12px] text-sand-500">{b.converted}/{b.leads}</span>
                      <span className={`font-display text-lg ${isTop ? "text-terra" : "text-sand-800"}`}>
                        {pct(b.conversion_rate)}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-cream-200 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${isTop ? "bg-terra" : "bg-sand-700"}`}
                      style={{ width: `${Math.min(100, widthPct)}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* 16-BL-HEATMAP (alle BLs, auch ohne Daten) */}
      {data && (
        <div data-testid="bl-stats-heatmap" className="rounded-3xl border border-cream-300 bg-white p-7">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="h-4 w-4 text-terra" strokeWidth={1.8} />
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              Heatmap · alle 16 Bundesländer
            </div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {data.by_bundesland.map((b) => {
              const intensity = b.leads / maxLeads; // 0..1
              const bg = b.leads === 0
                ? "bg-cream-100 text-sand-500"
                : intensity > 0.66
                  ? "bg-terra text-white"
                  : intensity > 0.33
                    ? "bg-terra/60 text-white"
                    : "bg-terra/25 text-sand-800";
              return (
                <div key={b.code} data-testid={`bl-stats-heat-${b.code}`}
                  title={`${b.name} · ${b.leads} Leads · ${pct(b.conversion_rate)} Conversion`}
                  className={`rounded-2xl px-3 py-3 text-center transition-colors ${bg}`}>
                  <div className="font-display text-base font-semibold leading-none">{b.code}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.16em] opacity-80">{b.leads}</div>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-[12px] text-sand-500">
            Farb-Intensität = Lead-Volumen relativ zum stärksten Bundesland. Hover für Details.
          </p>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div className="rounded-3xl border border-cream-300 bg-white p-6">
      <div className={`font-display text-4xl ${accent}`}>{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-sand-500">{label}</div>
    </div>
  );
}
